import { FC, ReactNode, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppAuth } from "@/auth/use-app-auth";
import { Loader2 } from "lucide-react";
import { useAuthFlow, getOrgFromLocation } from "@/lib/auth-flow";
import { useAuthProfile } from "@/hooks/useAuthProfile";

interface ProtectedRouteProps {
  children: ReactNode;
  skipOnboardingCheck?: boolean;
}

export const ProtectedRoute: FC<ProtectedRouteProps> = ({ children, skipOnboardingCheck }) => {
  const { isAuthenticated, isLoading } = useAppAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const startAuth = useAuthFlow();
  const orgAuthId = getOrgFromLocation();
  const {
    data: profile,
    isLoading: profileLoading,
    error: profileError,
  } = useAuthProfile(!skipOnboardingCheck);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      startAuth({
        mode: "login",
        returnTo: location.pathname + location.search,
        orgAuthId,
      });
    }
  }, [isLoading, isAuthenticated, startAuth, location.pathname, location.search, orgAuthId]);

  useEffect(() => {
    if (skipOnboardingCheck || !isAuthenticated || profileLoading) return;

    const status = (profileError as any)?.response?.status;
    const message = (profileError as any)?.response?.data?.message;

    if (status === 404 || message === "Recruiter not found") {
      navigate("/org-setup/onboarding");
      return;
    }

    if (profile && profile.onboardingCompleted === false) {
      navigate("/org-setup/onboarding");
    }
  }, [isAuthenticated, navigate, profile, profileError, profileLoading, skipOnboardingCheck]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="size-12 animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="size-12 animate-spin" />
      </div>
    );
  }

  if (!skipOnboardingCheck && profileLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 h-screen">
        <Loader2 className="size-12 animate-spin text-amber-600" />
        <p className="text-sm text-slate-500">Setting up your workspace...</p>
      </div>
    );
  }

  return <>{children}</>;
};
